import { Button as MuiButton, Stack } from '@mui/material'
import { FileDropArea, InfoBox, tokens } from 'ComponentLibrary'
import { ReactElement, useState } from 'react'
import { IconButtonWithOptions, Typography } from 'ComponentLibrary/src/piazza'
import { CheckIcon, FileIcon, UploadSimpleIcon, WarningIcon } from '@phosphor-icons/react'
import { TextWithEllipsis } from 'ComponentLibrary/src/piazza/text'
import { isFileAboveMaxSize } from 'ComponentLibrary/src/common/files'

export type FileProp = {
    name: string
    url: string
    content_type: string
}

type Props = {
    disabled?: boolean
    title: string
    size?: 'small' | 'medium'
    description?: string
    errorMessage?: string
    file: File | FileProp | undefined
    setFile: (file: File | undefined) => void
    allowedFileTypes?: string[]
    downloadFile?: () => void
    allowOverwrite?: boolean
    options?: Array<{ title: string; callback: () => void; danger?: boolean; icon?: ReactElement }>
    maxFileSizeBytes?: number
    fileSizeErrorMessage?: string
}

export function FileUploadCard({
    file,
    setFile,
    title,
    description,
    allowedFileTypes = ['application/pdf'],
    errorMessage,
    disabled = false,
    size = 'medium',
    downloadFile,
    allowOverwrite = true,
    options,
    maxFileSizeBytes,
    fileSizeErrorMessage,
}: Props) {
    const [isOver, setIsOver] = useState(false)
    const [fileValidationError, setFileValidationError] = useState<string | undefined>()
    const displayErrorMessage = fileValidationError ?? errorMessage
    const isFileSizeError = !!fileValidationError || (!!fileSizeErrorMessage && displayErrorMessage === fileSizeErrorMessage)

    const handleFile = (nextFile: File | undefined) => {
        if (nextFile && maxFileSizeBytes !== undefined && isFileAboveMaxSize(nextFile, maxFileSizeBytes)) {
            setFileValidationError(fileSizeErrorMessage ?? `Die Datei "${nextFile.name}" ist zu groß.`)
            return
        }
        setFileValidationError(undefined)
        setFile(nextFile)
    }

    return (
        <Stack direction="row" gap={tokens.SizingSpacing150} width="100%">
            <Stack direction="column" gap={tokens.SizingSpacing150} width="100%">
                <MuiButton
                    disabled={disabled}
                    disableRipple
                    disableElevation
                    component="label"
                    sx={{
                        pointerEvents: file && !allowOverwrite ? 'none' : undefined,
                        backgroundColor: displayErrorMessage
                            ? tokens.ColorSurfaceDangerDefault
                            : file
                              ? tokens.ColorSurfacePositiveDefault
                              : isOver
                                ? tokens.ColorSurfaceHover
                                : undefined,
                        width: '100%',
                        borderStyle: file || disabled ? 'solid' : undefined,
                        height: size === 'medium' ? '80px' : undefined,
                        backgroundImage:
                            file || disabled
                                ? undefined
                                : "url(\"data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' rx='8' ry='8' fill='none' stroke='%23e6e6e6ff' stroke-width='2' stroke-dasharray='8%2c 8' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e\");",
                        borderWidth: file || disabled ? tokens.SizingBorderS : undefined,
                        borderRadius: tokens.SizingCornerM,
                        borderColor: displayErrorMessage
                            ? tokens.ColorBorderDanger
                            : file
                              ? tokens.ColorBorderPositive
                              : tokens.ColorBorderDefault,
                        '&:hover': {
                            backgroundColor: tokens.ColorSurfaceHover,
                        },
                        '&.Mui-disabled': {
                            border: `${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault} !important`,
                        },
                        padding: size === 'medium' ? tokens.SizingSpacing200 : `${tokens.SizingSpacing075} ${tokens.SizingSpacing150}`,
                        justifyContent: 'start',
                        cursor: 'pointer',
                    }}
                >
                    <FileDropArea
                        allowedFileTypes={allowedFileTypes}
                        maxFileSizeBytes={maxFileSizeBytes}
                        fileSizeErrorMessage={fileSizeErrorMessage}
                        onFileValidationError={setFileValidationError}
                        setIsOver={setIsOver}
                        onDataDrop={(_areaId?: string, files?: File[]) => {
                            isOver && files && handleFile(files[0])
                        }}
                        width="100%"
                    >
                        <Stack
                            direction={size === 'medium' ? 'row' : 'row-reverse'}
                            gap={tokens.SizingSpacing150}
                            alignItems="center"
                            justifyContent={size === 'medium' ? 'space-between' : 'center'}
                            width="100%"
                        >
                            <Stack direction="column" gap={tokens.SizingSpacing075}>
                                <Typography variant={size === 'medium' ? 'headline3' : 'headline4'}>{title}</Typography>
                                {description && (
                                    <Typography variant="textS" color={tokens.ColorTextSubtle}>
                                        {description}
                                    </Typography>
                                )}
                            </Stack>
                            {
                                <input
                                    role="button"
                                    data-testid="file-upload-input"
                                    key={file ? file.name : 'file-upload-input'}
                                    hidden
                                    onChange={(e) => {
                                        if (e.target.files) {
                                            const files = Array.from(e.target.files)
                                            files.length && handleFile(files[0])
                                            e.target.value = ''
                                        }
                                    }}
                                    type="file"
                                    accept={allowedFileTypes.join(',')}
                                    disabled={disabled}
                                />
                            }
                            {file && !displayErrorMessage ? (
                                <CheckIcon size={tokens.SizingIconM} color={tokens.ColorIconPositive} style={{ flexShrink: 0 }} />
                            ) : (
                                <UploadSimpleIcon size={tokens.SizingIconM} style={{ flexShrink: 0 }} />
                            )}
                        </Stack>
                    </FileDropArea>
                </MuiButton>
                {file && <FileItem file={file} downloadFile={downloadFile} options={options} />}
                {displayErrorMessage && isFileSizeError ? (
                    <InfoBox variant="negative" icon={<WarningIcon />}>
                        {displayErrorMessage}
                    </InfoBox>
                ) : displayErrorMessage ? (
                    <Typography color={tokens.ColorTextDangerSubtle} variant="textS">
                        {displayErrorMessage}
                    </Typography>
                ) : null}
            </Stack>
        </Stack>
    )
}

type FileItemProps = {
    file: File | FileProp
    options?: Array<{ title: string; callback: () => void; danger?: boolean; icon?: ReactElement }>
    downloadFile?: () => void
}

const FileItem = ({ file, options, downloadFile }: FileItemProps) => {
    return (
        <Stack
            direction="row"
            gap={tokens.SizingSpacing075}
            padding={tokens.SizingSpacing100}
            bgcolor={tokens.ColorSurfaceAccentRoyalDefault}
            borderRadius={tokens.SizingCornerS}
            width="100%"
            justifyContent="space-between"
        >
            <Stack
                direction="row"
                gap={tokens.SizingSpacing075}
                alignItems="center"
                onClick={downloadFile}
                maxWidth={`calc(100% - 72px)`}
                sx={{
                    color: tokens.ColorTextBoldest,
                    cursor: downloadFile ? 'pointer' : undefined,
                    '&:hover': { color: downloadFile ? tokens.ColorTextLinkHover : undefined },
                }}
            >
                <FileIcon size={tokens.SizingIconS} weight="bold" style={{ flexShrink: 0 }} />
                <TextWithEllipsis typographyProps={{ variant: 'link' }} tooltipText={file.name}>
                    {file.name}
                </TextWithEllipsis>
            </Stack>
            {options && <IconButtonWithOptions variant="tertiary" tooltip="Optionen" size="small" options={options} />}
        </Stack>
    )
}
